import { useRef, useCallback } from 'react';
import { useMutation } from 'react-query';

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_DELAY = 3000;

const defaultIsRetryable = (error) => {
  const status = error?.status || error?.response?.status || 500;
  return status >= 500;
};

/**
 * Wraps an async action with automatic retry on server errors.
 * Delegates retry mechanism to React Query's `useMutation` internally —
 * requires `QueryClientProvider` in the component tree.
 *
 * Returns the action result on success, or `undefined` when all retries fail.
 * The `execute` function is referentially stable across re-renders.
 *
 * @param {(...args: any[]) => Promise<any>} action - Async function to retry. Receives whatever args are passed to `execute()`.
 * @param {Object} [options]
 * @param {number} [options.maxRetries=3] - Maximum retry attempts after the initial call.
 * @param {number} [options.delay=3000] - Fixed delay in ms between retries.
 * @param {(error: Error) => boolean} [options.isRetryable] - Predicate that determines if an error should trigger a retry. Defaults to HTTP 500+ (`error.status || error.response.status >= 500`).
 * @param {(error: Error, context: { isRetryExhausted: boolean, retryCount: number, status: number }) => void} [options.onError] - Called once when retries are exhausted or when the error is not retryable. Use this for toast notifications or error reporting.
 *
 * @returns {{ execute: (...args: any[]) => Promise<any|undefined>, isRetrying: boolean, retryCount: number, error: Error|null }}
 * - `execute` — Triggers the action. Returns the resolved value on success, `undefined` on failure.
 * - `isRetrying` — `true` while a retry is in-flight (not on the first attempt).
 * - `retryCount` — Number of failed retry attempts so far (`0` before any failure).
 * - `error` — The last error object after exhaustion, or `null` if no error / after a new `execute()` call.
 *
 * @example
 * // Hook setup — action + error handling
 * const { execute, isRetrying, error } = useRetryableAction(
 *   (id, payload) => updateBukuTarif(id, payload),
 *   {
 *     maxRetries: 3,
 *     delay: 3000,
 *     onError: (err, { isRetryExhausted, status }) => {
 *       const message = getErrorMessage(err);
 *       if (status >= 500 && isRetryExhausted) {
 *         showErrorToastWithRetry(`Update: ${message}`, () => handleUpdate());
 *       } else {
 *         toast.error(`Update: ${message}`);
 *       }
 *     },
 *   }
 * );
 *
 * // Caller — loading state managed externally
 * const handleUpdate = async () => {
 *   setLoading(true);
 *   const result = await execute(id, payload);
 *   if (result) {
 *     refetch();
 *     toast.success('Updated!');
 *   }
 *   setLoading(false);
 * };
 */
export const useRetryableAction = (action, options = {}) => {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    delay = DEFAULT_DELAY,
    isRetryable = defaultIsRetryable,
    onError,
  } = options;

  const actionRef = useRef(action);
  actionRef.current = action;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const isRetryableRef = useRef(isRetryable);
  isRetryableRef.current = isRetryable;

  const mutation = useMutation((args) => actionRef.current(...args), {
    retry: (failureCount, error) =>
      isRetryableRef.current(error) && failureCount < maxRetries,
    retryDelay: delay,
    onError: (error) => {
      const status = error?.status || error?.response?.status || 500;
      const canRetry = isRetryableRef.current(error);
      onErrorRef.current?.(error, {
        isRetryExhausted: canRetry,
        retryCount: mutation.failureCount + 1,
        status,
      });
    },
  });

  const mutateAsyncRef = useRef(mutation.mutateAsync);
  mutateAsyncRef.current = mutation.mutateAsync;

  const execute = useCallback(async (...args) => {
    try {
      return await mutateAsyncRef.current(args);
    } catch {
      return undefined;
    }
  }, []);

  const isRetrying = mutation.isLoading && mutation.failureCount > 0;

  return {
    execute,
    isLoading: mutation.isLoading,
    isRetrying,
    retryCount: mutation.failureCount,
    error: mutation.error ?? null,
  };
};
